import React, { createContext, useState, useEffect, useContext } from "react";
import {
  getAuth,
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { createUserDocument } from "./utils/firebaseUtils";
import {
  isUserAdmin,
  AdminConfig,
  DEFAULT_ADMIN_PERMISSIONS,
} from "./utils/adminUtils";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

export const db = getFirestore(app);
export const storage = getStorage(app);
export const auth = getAuth(app);
export const functions = getFunctions(app);

// Enhanced Auth context with admin functionality
interface AuthContextType {
  user: any;
  isAdmin: boolean;
  adminConfig: AdminConfig | null;
  signOut: () => void;
  refreshAdminStatus: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [adminConfig, setAdminConfig] = useState<AdminConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  const refreshAdminStatus = async () => {
    if (user) {
      // Check admin status based on email whitelist and dev mode
      const userIsAdmin = isUserAdmin(user.email);
      console.log(
        `🔄 Refreshing admin status for ${user.email}: ${userIsAdmin}`
      );
      setIsAdmin(userIsAdmin);

      if (userIsAdmin) {
        // Set admin config with default permissions (no Firestore needed)
        setAdminConfig({
          isAdmin: true,
          permissions: DEFAULT_ADMIN_PERMISSIONS,
          lastLoginAsAdmin: new Date().toISOString(),
        });
        console.log("✅ Admin access granted with default permissions");
      } else {
        setAdminConfig(null);
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Ensure user document exists in Firestore
        try {
          await createUserDocument(user);
        } catch (error) {
          console.error(
            "Error creating user document on auth state change:",
            error
          );
        }
      }
      setUser(user);

      // Check admin status
      if (user) {
        await refreshAdminStatus();
      } else {
        setIsAdmin(false);
        setAdminConfig(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  // Re-check admin status when user changes
  useEffect(() => {
    if (user) {
      refreshAdminStatus();
    }
  }, [user]);

  const signOut = () => {
    firebaseSignOut(auth).then(() => {
      setUser(null);
      setIsAdmin(false);
      setAdminConfig(null);
    });
  };

  const contextValue: AuthContextType = {
    user,
    isAdmin,
    adminConfig,
    signOut,
    refreshAdminStatus,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default app;

